import { useState, useRef, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router";
import { PageHeader } from "../../components/shared/PageHeader";
import { Badge } from "../../components/shared/Badge";
import { NewEnterpriseModal } from "../../components/shared/NewEnterpriseModal";
import { Plus, MoreHorizontal, Users, Shield, AppWindow, Settings, PauseCircle, PlayCircle } from "lucide-react";
import { usePlatformEnterprises, useSuspendEnterprise, useReinstateEnterprise } from "@platform/hooks";
import { useToast } from "@nthamizh/ui";
import { rootDomain } from "../../lib/subdomain";
import { cn } from "@nthamizh/ui";

interface Enterprise {
  id: string; name: string; slug: string;
  status: "active" | "suspended" | "pending"; createdAt: string;
}

interface EnterpriseMenuProps {
  enterprise: Enterprise;
  onSuspend: () => void;
  onReinstate: () => void;
  isPending: boolean;
}

function EnterpriseMenu({ enterprise, onSuspend, onReinstate, isPending }: EnterpriseMenuProps) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [menuStyle, setMenuStyle] = useState<React.CSSProperties>({});
  const btnRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  // Compute fixed position from button rect on open
  const handleOpen = useCallback(() => {
    if (btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect();
      const menuHeight = 220; // approx height of the dropdown
      const spaceBelow = window.innerHeight - rect.bottom;
      const top = spaceBelow >= menuHeight
        ? rect.bottom + 4
        : rect.top - menuHeight - 4;
      setMenuStyle({
        position: "fixed",
        top,
        right: window.innerWidth - rect.right,
        zIndex: 9999,
        width: 192,
      });
    }
    setOpen((v) => !v);
  }, []);

  // Close on outside click or scroll — exclude both the button and the portal menu
  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => {
      const target = e.target as Node;
      const insideBtn = btnRef.current?.contains(target);
      const insideMenu = menuRef.current?.contains(target);
      if (!insideBtn && !insideMenu) setOpen(false);
    };
    const closeOnScroll = () => setOpen(false);
    document.addEventListener("mousedown", close);
    document.addEventListener("scroll", closeOnScroll, true);
    return () => {
      document.removeEventListener("mousedown", close);
      document.removeEventListener("scroll", closeOnScroll, true);
    };
  }, [open]);

  const goTo = (path: string) => {
    setOpen(false);
    navigate(path);
  };

  const MENU_ITEMS = [
    {
      icon: Users, label: "Users",
      onClick: () => goTo(`/platform/enterprises/${enterprise.id}/users`),
    },
    {
      icon: Shield, label: "Roles",
      onClick: () => goTo(`/platform/enterprises/${enterprise.id}/roles`),
    },
    {
      icon: AppWindow, label: "Access level",
      onClick: () => goTo(`/platform/enterprises/${enterprise.id}/access`),
    },
    {
      icon: Settings, label: "Settings",
      onClick: () => goTo(`/platform/enterprises/${enterprise.id}/settings`),
    },
  ];

  return (
    <div className="relative">
      <button
        ref={btnRef}
        onClick={handleOpen}
        className={cn(
          "w-8 h-8 flex items-center justify-center rounded-lg transition-colors",
          open ? "bg-gray-200 text-gray-700" : "text-gray-400 hover:bg-gray-100 hover:text-gray-600",
        )}
        title="Enterprise options"
        aria-haspopup="true"
        aria-expanded={open}
      >
        <MoreHorizontal className="w-4 h-4" />
      </button>

      {open && createPortal(
        <div ref={menuRef} style={menuStyle} className="bg-white border border-ds-border rounded-xl shadow-lg py-1 overflow-hidden">
          {/* Enterprise navigation */}
          {MENU_ITEMS.map(({ icon: Icon, label, onClick }) => (
            <button
              key={label}
              onClick={onClick}
              className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-ds-text hover:bg-ds-bg transition-colors"
            >
              <Icon className="w-3.5 h-3.5 text-ds-text-muted flex-shrink-0" />
              {label}
            </button>
          ))}

          {/* Divider */}
          <div className="my-1 border-t border-ds-border" />

          {/* Suspend / Reinstate */}
          {enterprise.status === "active" ? (
            <button
              onClick={() => { setOpen(false); onSuspend(); }}
              disabled={isPending}
              className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors disabled:opacity-50"
            >
              <PauseCircle className="w-3.5 h-3.5 flex-shrink-0" />
              Suspend enterprise
            </button>
          ) : (
            <button
              onClick={() => { setOpen(false); onReinstate(); }}
              disabled={isPending}
              className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-emerald-700 hover:bg-emerald-50 transition-colors disabled:opacity-50"
            >
              <PlayCircle className="w-3.5 h-3.5 flex-shrink-0" />
              Reinstate enterprise
            </button>
          )}
        </div>, document.body
      )}
    </div>
  );
}

export function PlatformEnterprisesPage() {
  const [search, setSearch] = useState("");
  const [showNewModal, setShowNewModal] = useState(false);
  const { data: enterprises = [], isLoading } = usePlatformEnterprises();
  const suspend = useSuspendEnterprise();
  const reinstate = useReinstateEnterprise();
  const { toast } = useToast();

  const typed = enterprises as Enterprise[];
  const filtered = typed.filter((e) =>
    e.name.toLowerCase().includes(search.toLowerCase()) ||
    e.slug.includes(search.toLowerCase()),
  );

  const handleSuspend = async (id: string, name: string) => {
    try { await suspend.mutateAsync(id); toast({ title: `${name} suspended`, variant: "success" }); }
    catch { toast({ title: "Failed to suspend enterprise", variant: "error" }); }
  };

  const handleReinstate = async (id: string, name: string) => {
    try { await reinstate.mutateAsync(id); toast({ title: `${name} reinstated`, variant: "success" }); }
    catch { toast({ title: "Failed to reinstate enterprise", variant: "error" }); }
  };

  return (
    <div className="space-y-5">
      <PageHeader
        title="Enterprises"
        subtitle="All tenants on the platform"
        action={
          <button
            onClick={() => setShowNewModal(true)}
            className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700 transition-colors"
          >
            <Plus className="w-4 h-4" /> New enterprise
          </button>
        }
      />

      <div className="flex gap-3">
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search enterprises..."
          className="px-3 py-2 text-sm border border-ds-border rounded-lg focus:outline-none focus:border-indigo-400 w-64"
        />
      </div>

      <div className="bg-white border border-ds-border rounded-card shadow-card" style={{overflow: "visible"}}>
        {isLoading ? (
          <div className="p-8 text-center text-sm text-ds-text-muted">Loading...</div>
        ) : filtered.length === 0 ? (
          <div className="p-8 text-center text-sm text-ds-text-muted">No enterprises found.</div>
        ) : (
          <div style={{borderRadius:"inherit",overflow:"hidden"}}><table className="w-full">
            <thead>
              <tr className="border-b border-ds-border bg-ds-bg">
                <th className="text-left text-xs font-medium text-ds-text-muted uppercase tracking-wide px-5 py-3">Enterprise</th>
                <th className="text-left text-xs font-medium text-ds-text-muted uppercase tracking-wide px-5 py-3">Subdomain</th>
                <th className="text-left text-xs font-medium text-ds-text-muted uppercase tracking-wide px-5 py-3">Status</th>
                <th className="w-12 px-3 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-ds-border">
              {filtered.map((e) => (
                <tr key={e.id} className="hover:bg-ds-bg/50">
                  <td className="px-5 py-3.5">
                    <div className="flex items-center gap-2.5">
                      <div className="w-8 h-8 rounded-lg bg-indigo-600 flex items-center justify-center text-white text-xs font-semibold flex-shrink-0">
                        {e.name.slice(0, 2).toUpperCase()}
                      </div>
                      <div>
                        <p className="text-sm font-medium text-ds-text">{e.name}</p>
                        <p className="text-xs text-ds-text-muted">
                          {new Date(e.createdAt).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                  </td>
                  <td className="px-5 py-3.5 text-sm text-indigo-600">
                    {e.slug}.{rootDomain()}
                  </td>
                  <td className="px-5 py-3.5">
                    <Badge variant={
                      e.status === "active" ? "green" :
                      e.status === "suspended" ? "amber" : "gray"
                    }>
                      {e.status}
                    </Badge>
                  </td>
                  <td className="px-3 py-3.5 text-right">
                    <EnterpriseMenu
                      enterprise={e}
                      onSuspend={() => handleSuspend(e.id, e.name)}
                      onReinstate={() => handleReinstate(e.id, e.name)}
                      isPending={suspend.isPending || reinstate.isPending}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table></div>
        )}
      </div>

      <NewEnterpriseModal isOpen={showNewModal} onClose={() => setShowNewModal(false)} />
    </div>
  );
}
